import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { useForm } from 'react-final-form';
import { NewRadioButton } from '../../../NewRadioButton';
import { citiesListQuery, citiesPopularListQuery } from '../../../../queries';
import Loader from '../../../Loader';
import { NewSearchEmpty } from '../NewSearchEmpty';
import {
  setCitiesList,
  showPopupHeader,
  setChosenLocationNames,
  setCurrentCity,
  setError,
  setCurrentState,
  setLocationIds,
  setResetButtonIsDisabled,
  setAdriverSelectedCity
} from '../../../../actions';
import { entitiesFilter } from '../../../../utils/helpers/common';
import { FORM_VALUES } from '../../../../utils/helpers/constants';
import { City, NewSearchCityProps } from './types';
import {
  selectSearchFormCurrentCity,
  selectSearchFormCurrentState
} from '../../../../store/selectors';
import {
  AllCitiesContainer,
  CitiesContainer,
  PopularCitiesContainer,
  SectionCitiesTitle
} from './styled';
import { normalizeTargetingParameter } from '../../../../utils/helpers/normalizeTargetingParameter';
import { transliterateCyrillicToLatin } from '../../../../utils/helpers/transliterateCyrillicToLatin';

export const NewSearchCity = ({
  isCreate,
  isFocused,
  searchString,
  cancelHandler,
  closePopupHandler
}: NewSearchCityProps) => {
  const {
    t,
    i18n: { language }
  } = useTranslation();
  const dispatch = useDispatch();
  const { change, getState } = useForm();
  const { data, loading, error } = useQuery(citiesListQuery);
  const {
    data: popularCitiesData,
    loading: popularCitiesLoading,
    error: popularCitiesError
  } = useQuery(citiesPopularListQuery);
  const currentCity = useSelector(selectSearchFormCurrentCity);
  const storedFormState = useSelector(selectSearchFormCurrentState);

  const allCities = (data && data.cities) || [];
  const popularCities = (popularCitiesData && popularCitiesData.cities) || [];

  useEffect(() => {
    dispatch(setCitiesList(allCities));
  }, [data]);

  useEffect(() => {
    if (error) dispatch(setError(true));
  }, [error]);

  const onChangeRadioHandler = (city: City) => {
    const cityId = city.id;
    const cityName = city.name;
    const adriverCityName =
      language === 'az'
        ? normalizeTargetingParameter(cityName)
        : transliterateCyrillicToLatin(cityId, cityName);

    change('city_id', cityId);
    if (isCreate) {
      change(FORM_VALUES.locationId, undefined);
      change(FORM_VALUES.locationName, undefined);
    } else {
      dispatch(setCurrentCity(cityId));
      dispatch(setAdriverSelectedCity(adriverCityName));
      dispatch(
        setCurrentState({
          ...storedFormState,
          city_id: cityId,
          location_ids: undefined
        })
      );
      change('location_ids', undefined);
      dispatch(setLocationIds([]));
      dispatch(setResetButtonIsDisabled(false));
      dispatch(setChosenLocationNames([]));
    }
    dispatch(showPopupHeader());
    closePopupHandler(true, cityId);
    cancelHandler();
  };

  const allCitiesList = entitiesFilter(allCities, searchString).map(city => (
    <NewRadioButton
      key={city.id}
      data={city}
      onChangeHandler={() => onChangeRadioHandler(city)}
      currentValue={getState().values.city_id || currentCity}
      name="search-city"
      cypress="city"
      markerPosition="left"
    />
  ));

  const popularCitiesList = popularCities.map(city => (
    <NewRadioButton
      key={city.id}
      data={city}
      onChangeHandler={() => onChangeRadioHandler(city)}
      currentValue={getState().values.city_id || currentCity}
      name="popular-search-city"
      cypress="city"
      markerPosition="left"
    />
  ));

  return (
    <CitiesContainer $loading={loading || popularCitiesLoading}>
      <Loader loading={loading || popularCitiesLoading}>
        {!popularCitiesError && !searchString && !isFocused && (
          <>
            <SectionCitiesTitle tag="h3">
              {t('search.city_search.popular')}
            </SectionCitiesTitle>
            <PopularCitiesContainer>{popularCitiesList}</PopularCitiesContainer>
          </>
        )}

        {!!allCitiesList.length && !error && (
          <>
            {!searchString && (
              <SectionCitiesTitle tag="h3">
                {t('search.city_search.all_cities')}
              </SectionCitiesTitle>
            )}
            <AllCitiesContainer data-cy="new-search-popup__container">
              {allCitiesList}
            </AllCitiesContainer>
          </>
        )}
      </Loader>

      {!allCitiesList.length && searchString && (
        <NewSearchEmpty
          onClickHandler={cancelHandler}
          emptyText={t('search.city_search.empty_search')}
        />
      )}
    </CitiesContainer>
  );
};
